import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { signIn } from 'aws-amplify/auth';
import { useAuth } from '../useAuth';

export default function SignIn() {
  const navigate = useNavigate();
  const { user, loading, refresh } = useAuth();
  const [params] = useSearchParams();
  const [email, setEmail] = useState(params.get('email') || '');
  const [password, setPassword] = useState('');
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  // Already authenticated (e.g. left over from a previous tab) — skip the form.
  useEffect(() => {
    if (!loading && user) navigate('/dashboard', { replace: true });
  }, [loading, user, navigate]);

  async function onSubmit(e) {
    e.preventDefault();
    setErr(null);
    setBusy(true);
    try {
      const result = await signIn({ username: email, password });
      if (result.nextStep?.signInStep === 'CONFIRM_SIGN_UP') {
        navigate(`/verify?email=${encodeURIComponent(email)}`);
        return;
      }
      await refresh();
      navigate('/dashboard', { replace: true });
    } catch (e) {
      // If a session is already active, Amplify throws
      // UserAlreadyAuthenticatedException — that's still a successful state.
      if (e?.name === 'UserAlreadyAuthenticatedException') {
        await refresh();
        navigate('/dashboard', { replace: true });
        return;
      }
      setErr(e.message || 'Sign-in failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="min-h-screen bg-neutral-950 text-neutral-100 flex items-center justify-center px-6">
      <form onSubmit={onSubmit} className="w-full max-w-sm space-y-4">
        <h1 className="text-3xl font-semibold mb-6">Sign in</h1>
        <input
          type="email"
          required
          placeholder="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="w-full px-3 py-2 rounded bg-neutral-900 border border-neutral-800 focus:border-indigo-500 outline-none"
        />
        <input
          type="password"
          required
          placeholder="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="w-full px-3 py-2 rounded bg-neutral-900 border border-neutral-800 focus:border-indigo-500 outline-none"
        />
        {err && <p className="text-sm text-red-400">{err}</p>}
        <button
          type="submit"
          disabled={busy}
          className="w-full px-4 py-2 rounded bg-indigo-500 hover:bg-indigo-400 disabled:opacity-50 text-white font-medium"
        >
          {busy ? 'Signing in…' : 'Sign in'}
        </button>
        <p className="text-sm text-neutral-400 text-center">
          No account?{' '}
          <Link to="/signup" className="text-indigo-400 hover:text-indigo-300">
            Create one
          </Link>
        </p>
      </form>
    </main>
  );
}
